import { Table, Typography } from "antd";
import type { SortOrder } from "antd/es/table/interface";
import styles from "./order.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import ColumnHeader from "../common/ColumnHeader";
import moment from "moment";
import { NoRecordIcon } from "../../assets/icons/NoRecordIcon";

const { Text } = Typography;

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const cellTooltip = (value: any) => ({
  title: value,
  styles: {
    root: { maxWidth: "none" },
    container: { whiteSpace: "nowrap" as const },
  },
});

interface OrderSummaryProps {
  orders: any;
  pagination: { page: number; limit: number };
  totalOrders: number;
  onPaginationChange: (page: number, limit: number) => void;
  sortField?: string | null;
  sortOrder?: "asc" | "desc" | null;
  onSortChange?: (sortField: string | null, sortOrder: "asc" | "desc" | null) => void;
  onRowClick: (itemId: string) => void;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  orders,
  pagination,
  totalOrders,
  onPaginationChange,
  sortField,
  sortOrder,
  onSortChange,
  onRowClick,
}) => {
  const handleTableChange = (_pagination: any, _filters: any, sorter: any, extra: any) => {
    if (extra.action === "sort") {
      const field: string | null = sorter.field ?? null;
      const order: "asc" | "desc" | null =
        sorter.order === "ascend" ? "asc" : sorter.order === "descend" ? "desc" : null;
      onSortChange?.(field, order);
    }
  };

  const getSortOrder = (field: string): SortOrder =>
    sortField === field
      ? sortOrder === "asc"
        ? "ascend"
        : sortOrder === "desc"
          ? "descend"
          : null
      : null;

  const sortIcon = ({ sortOrder: so }: any) => {
    if (so === "ascend")
      return <FontAwesomeIcon icon={faArrowUp} style={{ height: "12px" }} />;
    if (so === "descend")
      return <FontAwesomeIcon icon={faArrowDown} style={{ height: "12px" }} />;
    return <></>;
  };


  const columns = [
    {
      title: <ColumnHeader tooltip="Unique ID your system assigned to this item within your order" label="ITEM REF ID" />,
      dataIndex: "itemReferenceId",
      key: "itemReferenceId",
      width: 320,
      fixed: "left" as const,
      sorter: true,
      sortOrder: getSortOrder("itemReferenceId"),
      sortIcon,
      render: (_: any, record: any) => {
        const value = record.itemReferenceId ? record.itemReferenceId : "-";
        return <Text ellipsis={{ tooltip: cellTooltip(value) }}>{value}</Text>;
      },
    },
    {
      title: <ColumnHeader tooltip="ID our API assigned to this item" label="ITEM ID" />,
      dataIndex: "itemId",
      key: "itemId",
      width: 130,
      sorter: true,
      sortOrder: getSortOrder("itemId"),
      sortIcon,
      render: (_: any, record: any) => {
        const value = record.itemId ? record.itemId : "-";
        return <Text ellipsis={{ tooltip: cellTooltip(value) }}>{value}</Text>;
      },
    },
    {
      title: (
        <ColumnHeader
          tooltip="ID Xpressdocs assigned to your order"
          label="XD ORDER ID"
        />
      ),
      dataIndex: "xdOrderId",
      key: "xdOrderId",
      width: 150,
      sorter: true,
      sortOrder: getSortOrder("xdOrderId"),
      sortIcon,
      render: (_: any, record: any) => {
        const value = record.xdOrderId ? record.xdOrderId : "-";
        return <Text ellipsis={{ tooltip: cellTooltip(value) }}>{value}</Text>;
      },
    },
    {
      title: "DATE SUBMITTED",
      dataIndex: "dateSubmitted",
      key: "dateSubmitted",
      width: 200,
      sorter: true,
      sortOrder: getSortOrder("dateSubmitted"),
      sortIcon,
      render: (_: any, record: any) => {
        const value = moment(record.dateSubmitted).format(
          "MM/DD/YYYY hh:mm:ss A",
        );
        return <Text ellipsis={{ tooltip: cellTooltip(value) }}>{value}</Text>;
      },
    },
    {
      title: (
        <ColumnHeader
          tooltip="Total pieces submitted at the time of order, before any processing or validation."
          label="REQUESTED QTY"
        />
      ),
      dataIndex: "quantity",
      key: "quantity",
      width: 200,
      sorter: true,
      sortOrder: getSortOrder("quantity"),
      sortIcon,
      render: (_: any, record: any) => {
        const value = record.quantity ? record.quantity.toLocaleString() : "-";
        return <Text ellipsis={{ tooltip: cellTooltip(value) }}>{value}</Text>;
      },
    },
    {
      title: (
        <ColumnHeader
          tooltip="Pieces successfully processed. Any failed or cancelled pieces are shown in the Invalid sub-column. May still change for orders currently in progress."
          label="FINAL QTY"
        />
      ),
      dataIndex: "recipients",
      key: "recipients",
      width: 150,
      sorter: true,
      sortOrder: getSortOrder("recipients"),
      sortIcon,
      render: (_: any, record: any) => {
        const total = record.recipients?.total || record.recipients?.total === 0
          ? `${record.recipients.total.toLocaleString()}`
          : "-";
        const valid = record.recipients?.valid || record.recipients?.valid === 0
          ? `${record.recipients.valid.toLocaleString()}`
          : "-";
        const invalid = record.recipients?.invalid
          ? `${record.recipients.invalid.toLocaleString()} invalid`
          : "";
        const value = invalid ? `${total} (${invalid})` : total;
        return (
          <Text ellipsis={{ tooltip: cellTooltip(value) }}>
            {valid? valid : total}
            {invalid && <span className={styles.invalidCount}>{invalid}</span>}
          </Text>
        );
      },
    },
    {
      title: "ORDER TOTAL",
      dataIndex: "orderTotal",
      key: "orderTotal",
      width: 200,
      sorter: false,
      render: (_: any, record: any) => {
        const value = record.orderTotal ? "$ " + record.orderTotal : "-";
        return <Text ellipsis={{ tooltip: cellTooltip(value) }}>{value}</Text>;
      },
    },
    {
      title: <ColumnHeader tooltip="Status of orders" label="ORDER STATUS" />,
      dataIndex: "orderStatus",
      key: "orderStatus",
      width: 200,
      sorter: true,
      sortOrder: getSortOrder("orderStatus"),
      sortIcon,
      render: (_: any, record: any) => (
        <div
          title={record.orderStatus}
          className={
            record.orderStatus.toLowerCase() === "processing"
              ? styles.processing
              : record.orderStatus.toLowerCase() === "error" ||
                  record.orderStatus.toLowerCase() === "cancelled"
                ? styles.error
                : styles.regular
          }
        >
          {record.orderStatus}
        </div>
      ),
    },
    {
      title: "DELIVERY STATUS",
      dataIndex: "deliveryStatus",
      key: "deliveryStatus",
      width: 175,
      sorter: true,
      sortOrder: getSortOrder("deliveryStatus"),
      sortIcon,
      render: (_: any, record: any) => {
        const value = record.deliveryStatus ? record.deliveryStatus : "-";
        return <Text ellipsis={{ tooltip: cellTooltip(value) }}>{value}</Text>;
      },
    },
    {
      title: "SIZE",
      dataIndex: "size",
      key: "size",
      width: 100,
      sorter: true,
      sortOrder: getSortOrder("size"),
      sortIcon,
      render: (_: any, record: any) => {
        const value = record.template?.size ? record.template.size : "-";
        return <Text ellipsis={{ tooltip: cellTooltip(value) }}>{value}</Text>;
      },
    },
    {
      title: "DELIVERY METHOD",
      dataIndex: "deliveryMethod",
      key: "deliveryMethod",
      width: 175,
      sorter: true,
      sortOrder: getSortOrder("deliveryMethod"),
      sortIcon,
      render: (_: any, record: any) => {
        const value = record.deliveryMethod ? record.deliveryMethod : "-";
        return <Text ellipsis={{ tooltip: cellTooltip(value) }}>{value}</Text>;
      },
    },
    {
      title: "TEMPLATE",
      dataIndex: "templateName",
      key: "templateName",
      width: 250,
      sorter: true,
      sortOrder: getSortOrder("templateName"),
      sortIcon,
      render: (_: any, record: any) => {
        const value = record.template?.templateName
          ? record.template.templateName
          : "-";
        return <Text ellipsis={{ tooltip: cellTooltip(value) }}>{value}</Text>;
      },
    },
  ];

  const emptyState = (
    <div className={styles.emptyState}>
      <div className={styles.emptyStateIcon}>
        <NoRecordIcon/>
      </div>
      <div className={styles.emptyStateTitle}>No Rows found</div>
      <div className={styles.emptyStateSubtext}>
        Try adjusting your filters or search criteria.
      </div>
    </div>
  );

  const hidePagination = totalOrders <= PAGE_SIZE_OPTIONS[0];

  return (
    <div className={styles.orderTable}>
      <Table
        dataSource={orders}
        columns={columns}
        pagination={
          hidePagination
            ? false
            : {
                current: pagination.page,
                pageSize: pagination.limit,
                total: totalOrders,
                showSizeChanger: { showSearch: false },
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                locale: { items_per_page: "" },
                showTotal: (total) => `${total} RESULTS`,
                onChange: (page, pageSize) =>
                  onPaginationChange(pageSize !== pagination.limit ? 1 : page, pageSize),
              }
        }
        onChange={handleTableChange}
        showHeader={!!orders?.length}
        showSorterTooltip={false}
        sticky={true}
        tableLayout="fixed"
        locale={{ emptyText: emptyState }}
        scroll={{
          x: 2060,
          y: (orders?.length && !hidePagination) ? "calc(100vh - 385px)" :
          (hidePagination ? "calc(100vh - 335px)" : "calc(100vh - 290px)"),
        }}
        onRow={(record) => ({
          onClick: () => onRowClick(record.itemId),
          style: { cursor: "pointer" },
        })}
      />
    </div>
  );
};

export default OrderSummary;
